import { Fragment } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { toggleMark } from '@tiptap/pm/commands';
import { getMarkRange } from '@tiptap/react';
import { safeHref, safeImageSrc } from '../../markdown/wikiMarkdown';
import { isCellNode, isItemNode, LIST_KIND_BY_NODE, LIST_NODE_BY_KIND } from './schema';

/**
 * The prototype's `wkWysiCmd` on a ProseMirror transaction.
 *
 * The prototype treats the document as a flat run of blocks: each top-level
 * paragraph / heading / quote / callout / code block is one block, and each
 * list item is one block of a list run. Commands here work on exactly that
 * model ("units"): the top-level nodes around the selection are decomposed
 * into units, the targets are re-kinded, consecutive list units of the same
 * kind are regrouped into one list (which also merges with an adjacent list
 * of the same kind, prototype `wkToList`), and the range is replaced. The
 * caret is restored by (textblock ordinal, offset) — every transform maps
 * textblocks 1:1 in document order, so this is the prototype's
 * "measure the caret offset, then wkSetCaret" without DOM ranges.
 *
 * Every function takes a Transaction and returns true when it changed it, so
 * the input rules can chain them inside one transaction.
 */

export const ZWSP_RE = new RegExp(String.fromCharCode(0x200b), 'g');
export const BLOCK_KINDS = Object.freeze(['p', 'h1', 'h2', 'h3', 'quote', 'note', 'warn', 'pre']);
export const LIST_KINDS = Object.freeze(['ul', 'ol', 'task']);
export const MARK_KINDS = Object.freeze({ bold: 'bold', italic: 'italic', strike: 'strike' });

const isListNode = (node) => !!node && Boolean(LIST_KIND_BY_NODE[node.type.name]);
const clean = (s) => String(s || '').replace(ZWSP_RE, '');

/** Block kind of a top-level textblock: p | h1..h3 | quote | note | warn | pre (null otherwise). */
export function textblockKind(node) {
    if (!node) return null;
    switch (node.type.name) {
        case 'paragraph': return 'p';
        case 'heading': return `h${Math.min(3, Math.max(1, node.attrs.level || 1))}`;
        case 'blockquote': return node.attrs.callout === 'warn' ? 'warn' : node.attrs.callout === 'note' ? 'note' : 'quote';
        case 'codeBlock': return 'pre';
        default: return null;
    }
}

/**
 * The caret block's kind for the toolbar (prototype `wkBlockKind` of
 * `wkCurBlock`): table | ul | ol | task | p | h1..h3 | quote | note | warn | pre.
 */
export function currentBlockKind(state) {
    const { $from } = state.selection;
    const parent = $from.parent;
    if (isCellNode(parent)) return 'table';
    if (isItemNode(parent) && $from.depth >= 2) return LIST_KIND_BY_NODE[$from.node($from.depth - 1).type.name] || 'ul';
    if (state.selection instanceof NodeSelection && state.selection.node.type.name === 'table') return 'table';
    return textblockKind(parent) || 'p';
}

/** Whether every text character in the selection (or the caret's marks) carries `type`. */
export function isMarkActive(state, type) {
    if (!type) return false;
    const { empty, $from, from, to } = state.selection;
    if (empty) return Boolean(type.isInSet(state.storedMarks || $from.marks()));
    let seen = false;
    let all = true;
    state.doc.nodesBetween(from, to, (node) => {
        if (!node.isText) return;
        seen = true;
        if (!type.isInSet(node.marks)) all = false;
    });
    return seen && all;
}

/**
 * The caret's toolbar key, prototype `wkEdCur` = "<blockKind>|<fmt>"
 * (fmt = b/i/s). Bold/italic/strike only read as active outside headings.
 */
export function currentToolbarKey(state) {
    const kind = currentBlockKind(state);
    const { marks } = state.schema;
    let fmt = '';
    if (!/^h/.test(kind)) {
        if (isMarkActive(state, marks.bold)) fmt += 'b';
        if (isMarkActive(state, marks.italic)) fmt += 'i';
        if (isMarkActive(state, marks.strike)) fmt += 's';
    }
    return `${kind}|${fmt}`;
}

/* ---------------------------------------------------------------- units */

function posOfIndex(doc, index) {
    let pos = 0;
    for (let i = 0; i < index; i += 1) pos += doc.child(i).nodeSize;
    return pos;
}

function topRange(doc, selection) {
    const last = doc.childCount - 1;
    const $from = doc.resolve(selection.from);
    const $to = doc.resolve(selection.to);
    let lo = Math.min($from.index(0), last);
    let hi = $to.index(0);
    if ($to.depth === 0 && hi > lo) hi -= 1;
    hi = Math.min(Math.max(hi, lo), last);
    return [lo, hi];
}

function collectUnits(doc, lo, hi) {
    const units = [];
    let pos = posOfIndex(doc, lo);
    for (let i = lo; i <= hi; i += 1) {
        const node = doc.child(i);
        const listKind = LIST_KIND_BY_NODE[node.type.name];
        if (listKind) {
            let p = pos + 1;
            node.forEach((item) => {
                units.push({
                    kind: listKind, node: item, checked: Boolean(item.attrs.checked), listId: i,
                    start: p + 1, end: p + 1 + item.content.size,
                });
                p += item.nodeSize;
            });
        } else if (textblockKind(node)) {
            units.push({ kind: textblockKind(node), node, start: pos + 1, end: pos + 1 + node.content.size });
        } else {
            units.push({ kind: 'other', node, start: pos, end: pos + node.nodeSize });
        }
        pos += node.nodeSize;
    }
    return units;
}

function pickTargets(units, selection) {
    const textUnits = units.filter((u) => u.kind !== 'other');
    if (selection.empty) {
        const { $from } = selection;
        if (!$from.parent.isTextblock || isCellNode($from.parent)) return [];
        return textUnits.filter((u) => u.start <= $from.pos && $from.pos <= u.end).slice(0, 1);
    }
    const { from, to } = selection;
    return textUnits.filter((u) => {
        if (u.start > to || u.end < from) return false;
        // A selection that merely touches a block's edge doesn't include it.
        if (u.end > u.start && (u.start === to || u.end === from)) return false;
        return true;
    });
}

function inlineContent(schema, node, toKind) {
    const fromCode = node.type.name === 'codeBlock';
    if (toKind === 'pre') {
        if (fromCode) return node.content;
        // Flatten to plain text: line breaks become newlines, formatting is lost.
        let text = '';
        node.forEach((child) => {
            if (child.isText) text += child.text;
            else if (child.type.name === 'hardBreak') text += '\n';
        });
        return text ? Fragment.from(schema.text(text)) : Fragment.empty;
    }
    if (!fromCode) return node.content;
    const parts = [];
    node.textContent.split('\n').forEach((line, i) => {
        if (i) parts.push(schema.nodes.hardBreak.create());
        if (line) parts.push(schema.text(line));
    });
    return Fragment.fromArray(parts);
}

function buildTextblock(schema, unit) {
    const kind = unit.toKind || unit.kind;
    const content = inlineContent(schema, unit.node, kind);
    const n = schema.nodes;
    switch (kind) {
        case 'h1': case 'h2': case 'h3': return n.heading.create({ level: Number(kind[1]) }, content);
        case 'quote': return n.blockquote.create({ callout: null }, content);
        case 'note': case 'warn': return n.blockquote.create({ callout: kind }, content);
        case 'pre': return n.codeBlock.create(null, content);
        case 'ul': case 'ol': return n.listItem.create(null, content);
        case 'task': return n.taskItem.create({ checked: Boolean(unit.toChecked !== undefined ? unit.toChecked : unit.checked) }, content);
        default: return n.paragraph.create(null, content);
    }
}

function composeUnits(schema, units) {
    const out = [];
    let run = null;
    const flush = () => {
        if (run) out.push(schema.nodes[LIST_NODE_BY_KIND[run.kind]].create(null, run.items));
        run = null;
    };
    units.forEach((u) => {
        const kind = u.toKind || u.kind;
        if (LIST_KINDS.includes(kind)) {
            if (!run || run.kind !== kind) { flush(); run = { kind, items: [] }; }
            run.items.push(buildTextblock(schema, u));
        } else {
            flush();
            out.push(kind === 'other' ? u.node : buildTextblock(schema, u));
        }
    });
    flush();
    return out;
}

function locate(doc, from, to, pos) {
    let ordinal = -1;
    let found = null;
    doc.nodesBetween(from, to, (node, p) => {
        if (found) return false;
        if (node.isTextblock) {
            ordinal += 1;
            const s = p + 1;
            const e = s + node.content.size;
            if (pos >= s && pos <= e) found = { ordinal, offset: pos - s };
            return false;
        }
        return true;
    });
    return found;
}

function relocate(doc, from, to, mark) {
    let ordinal = -1;
    let found = null;
    doc.nodesBetween(from, to, (node, p) => {
        if (found !== null) return false;
        if (node.isTextblock) {
            ordinal += 1;
            if (ordinal === mark.ordinal) found = Math.min(p + 1 + mark.offset, p + 1 + node.content.size);
            return false;
        }
        return true;
    });
    return found;
}

/** Replace top-level children lo..hi with `nodes`, keeping the selection on the same text. */
function replaceTopLevel(tr, lo, hi, nodes) {
    const start = posOfIndex(tr.doc, lo);
    const end = posOfIndex(tr.doc, hi + 1);
    const sel = tr.selection;
    const anchor = locate(tr.doc, start, end, sel.anchor);
    const head = locate(tr.doc, start, end, sel.head);
    tr.replaceWith(start, end, nodes);
    const newEnd = start + Fragment.fromArray(nodes).size;
    const a = anchor ? relocate(tr.doc, start, newEnd, anchor) : null;
    const h = head ? relocate(tr.doc, start, newEnd, head) : null;
    if (a !== null && h !== null) tr.setSelection(TextSelection.create(tr.doc, a, h));
}

/**
 * Block and list kinds (toolbar Styles + Lists groups, slash items, block
 * input rules, Mod-Alt-0..3). Prototype semantics:
 * - block kinds: all targets already that kind (and not `p`) → back to text;
 *   list items are lifted out of their list first (the list splits).
 * - list kinds: all targets already items of that list → lift to text; an item
 *   of another list kind retypes its WHOLE list (once per list); any other
 *   block becomes an item (unchecked), merging with adjacent same-kind lists.
 */
export function applyBlockKind(tr, kind) {
    const isBlock = BLOCK_KINDS.includes(kind);
    if (!isBlock && !LIST_KINDS.includes(kind)) return false;
    const { doc } = tr;
    if (!doc.childCount) return false;
    let [lo, hi] = topRange(doc, tr.selection);
    if (lo > 0 && isListNode(doc.child(lo - 1))) lo -= 1;
    if (hi < doc.childCount - 1 && isListNode(doc.child(hi + 1))) hi += 1;
    const units = collectUnits(doc, lo, hi);
    const targets = pickTargets(units, tr.selection);
    if (!targets.length) return false;
    if (isBlock) {
        const allSame = targets.every((t) => t.kind === kind);
        const next = allSame && kind !== 'p' ? 'p' : kind;
        targets.forEach((t) => { t.toKind = next; });
    } else if (targets.every((t) => t.kind === kind)) {
        targets.forEach((t) => { t.toKind = 'p'; });
    } else {
        const retyped = new Set();
        targets.forEach((t) => {
            if (LIST_KINDS.includes(t.kind)) {
                if (t.kind !== kind && !retyped.has(t.listId)) {
                    retyped.add(t.listId);
                    units.filter((u) => u.listId === t.listId).forEach((u) => {
                        u.toKind = kind;
                        // A plain item has no checked state; one retyped into a checklist starts unchecked.
                        u.toChecked = u.kind === 'task' ? u.checked : false;
                    });
                }
                return;
            }
            t.toKind = kind;
            t.toChecked = false;
        });
    }
    replaceTopLevel(tr, lo, hi, composeUnits(tr.doc.type.schema, units));
    return true;
}

/* -------------------------------------------------------------- inserts */

/** The prototype's new-table markup: header Column 1..3 + 2 empty rows. */
export function createTable(schema, headers = ['Column 1', 'Column 2', 'Column 3'], rows = 2) {
    const n = schema.nodes;
    const head = n.tableHead.create(null, n.tableRow.create(null, headers.map((h) => n.tableHeader.create(null, h ? schema.text(h) : null))));
    const body = n.tableBody.create(null, Array.from({ length: rows }, () => n.tableRow.create(null, headers.map(() => n.tableCell.create()))));
    return n.table.create(null, [head, body]);
}

/**
 * Insert block nodes after the selection's top-level block, followed by a new
 * empty paragraph (prototype `wkInsertBlockEl`). An empty paragraph/heading is
 * replaced instead. The caret lands in the new paragraph, or — for a table —
 * the first header cell's text is selected so typing replaces "Column 1".
 */
export function insertBlocks(tr, nodes, { selectFirstHeader = false } = {}) {
    const { schema } = tr.doc.type;
    const { doc } = tr;
    const idx = Math.min(tr.selection.$from.index(0), doc.childCount - 1);
    const top = doc.child(idx);
    const topPos = posOfIndex(doc, idx);
    const empty = (top.type.name === 'paragraph' || top.type.name === 'heading') && !clean(top.textContent).trim();
    const after = schema.nodes.paragraph.create();
    const base = empty ? topPos : topPos + top.nodeSize;
    if (empty) tr.replaceWith(topPos, topPos + top.nodeSize, [...nodes, after]);
    else tr.insert(base, [...nodes, after]);
    const size = nodes.reduce((s, n) => s + n.nodeSize, 0);
    if (selectFirstHeader && nodes[0] && nodes[0].type.name === 'table') {
        const cell = nodes[0].firstChild.firstChild.firstChild;
        const s = base + 4;
        tr.setSelection(TextSelection.create(tr.doc, s, s + cell.content.size));
    } else {
        tr.setSelection(TextSelection.create(tr.doc, base + size + 1));
    }
    return true;
}

/* --------------------------------------------------------------- inline */

/**
 * The range of `type` the caret is "in": inside a marked run, or at its start
 * (where the browser caret sits inside the element too). At the run's end the
 * caret is after it — typing there is plain text.
 */
export function markRangeAtCaret($pos, type) {
    const after = $pos.nodeAfter;
    // The character after the caret carries the mark: the caret is inside the
    // run or at its start. Only the run's end (mark before, none after) is out.
    if (!after || !type.isInSet(after.marks)) return null;
    return getMarkRange($pos, type) || null;
}

/** Ctrl/Cmd+E and the `</>` button (prototype: unwrap inside code, else wrap the selection or insert "code"). */
export function toggleInlineCode(tr) {
    const { schema } = tr.doc.type;
    const code = schema.marks.code;
    const { $from, from, to, empty } = tr.selection;
    if (!$from.parent.inlineContent || !$from.parent.type.allowsMarkType(code)) return false;
    const range = markRangeAtCaret($from, code);
    if (range) {
        tr.removeMark(range.from, range.to, code);
        return true;
    }
    const text = (empty ? '' : tr.doc.textBetween(from, to, ' ', ' ')).replace(/\n/g, ' ') || 'code';
    tr.replaceSelectionWith(schema.text(text, [code.create()]), false);
    tr.setStoredMarks([]);
    return true;
}

/** Bold / italic / strike — ProseMirror's toggleMark (the prototype used execCommand). */
export function toggleMarkKind(kind) {
    return (state, dispatch) => {
        const type = state.schema.marks[MARK_KINDS[kind]];
        return type ? toggleMark(type)(state, dispatch) : false;
    };
}

/** Prototype link normalisation: no scheme and not `/…` or `#…` → `https://`. */
export function normaliseLinkUrl(raw) {
    const url = String(raw || '').trim();
    if (!url) return '';
    if (!/^[a-z][a-z0-9+.-]*:/i.test(url) && !/^[/#]/.test(url)) return `https://${url}`;
    return url;
}

/** What the link bar opens with (prototype `wkLink`): the caret link's href, and the selected text. */
export function linkContext(state, kind) {
    const { from, to, $from } = state.selection;
    const link = state.schema.marks.link;
    let url = '';
    if (kind === 'link') {
        const range = markRangeAtCaret($from, link);
        if (range) {
            const node = state.doc.nodeAt(range.from);
            const mark = node && link.isInSet(node.marks);
            url = (mark && mark.attrs.href) || '';
        }
    }
    return { kind, from, to, url, text: state.doc.textBetween(from, to, '\n', '') };
}

/**
 * Apply the link bar (prototype `wkLinkApply`). `ctx.from/to` is the selection
 * saved when the bar opened (kept mapped by the editor). Image and video
 * insert a block after it; link updates the caret's link, links the selected
 * text, or inserts the URL (without http(s)://) as a link. Safety: an image
 * that is not http(s) / a wiki asset becomes its literal Markdown text (as the
 * engine does), and a link with an unsafe scheme is not applied.
 */
export function applyLinkBar(tr, ctx, rawUrl) {
    const url = normaliseLinkUrl(rawUrl);
    const { schema } = tr.doc.type;
    const max = tr.doc.content.size;
    if (ctx && Number.isFinite(ctx.from)) {
        const from = Math.max(0, Math.min(ctx.from, max));
        const to = Math.max(from, Math.min(ctx.to, max));
        try {
            tr.setSelection(TextSelection.create(tr.doc, from, to));
        } catch { /* keep the current selection */ }
    }
    if (!url) return false;
    const kind = ctx && ctx.kind;
    if (kind === 'image') {
        const node = safeImageSrc(url)
            ? schema.nodes.image.create({ src: url, alt: '' })
            : schema.nodes.paragraph.create(null, schema.text(`![](${url})`));
        return insertBlocks(tr, [node]);
    }
    if (kind === 'video') return insertBlocks(tr, [schema.nodes.embed.create({ url })]);
    const link = schema.marks.link;
    const { $from, from, to, empty } = tr.selection;
    if (!safeHref(url) || !$from.parent.inlineContent || !$from.parent.type.allowsMarkType(link)) return false;
    const range = markRangeAtCaret($from, link);
    if (range) {
        tr.addMark(range.from, range.to, link.create({ href: url }));
        return true;
    }
    if (!empty) {
        tr.addMark(from, to, link.create({ href: url }));
        return true;
    }
    const marks = $from.marks().filter((m) => m.type !== link).concat(link.create({ href: url }));
    tr.replaceSelectionWith(schema.text(url.replace(/^https?:\/\//, ''), marks), false);
    tr.setStoredMarks($from.marks().filter((m) => m.type !== link));
    return true;
}

/* ---------------------------------------------------------------- tables */

function cellDepth($pos) {
    for (let d = $pos.depth; d > 0; d -= 1) if (isCellNode($pos.node(d))) return d;
    return 0;
}

function tableCells(table, tablePos) {
    const cells = [];
    table.descendants((node, p) => {
        if (isCellNode(node)) { cells.push({ node, pos: tablePos + 1 + p }); return false; }
        return true;
    });
    return cells;
}

function placeInCell(tr, cell, select) {
    const start = cell.pos + 1;
    const end = start + cell.node.content.size;
    if (select && clean(cell.node.textContent).trim()) tr.setSelection(TextSelection.create(tr.doc, start, end));
    else tr.setSelection(TextSelection.create(tr.doc, start));
}

/**
 * Tab / Shift+Tab / Enter in a table cell (prototype `wkWysiKeyH`): next /
 * previous / the cell directly below. Past the last cell a row is appended
 * and the caret goes into it. A target cell with text has its text selected.
 */
export function moveTableCell(tr, dir) {
    const { $from } = tr.selection;
    const d = cellDepth($from);
    if (!d || d < 4) return false;
    const row = $from.node(d - 1);
    const tableDepth = d - 3;
    const table = $from.node(tableDepth);
    const tablePos = $from.before(tableDepth);
    const cellPos = $from.before(d);
    const cells = tableCells(table, tablePos);
    const current = cells.findIndex((c) => c.pos === cellPos);
    let i = current + (dir === 'down' ? row.childCount : dir === 'prev' ? -1 : 1);
    if (i >= cells.length) {
        const { schema } = tr.doc.type;
        const newRow = schema.nodes.tableRow.create(null, Array.from({ length: row.childCount }, () => schema.nodes.tableCell.create()));
        const bodyEnd = tablePos + 1 + table.child(0).nodeSize + table.child(1).nodeSize - 1;
        tr.insert(bodyEnd, newRow);
        const nextCells = tableCells(tr.doc.nodeAt(tablePos), tablePos);
        i = Math.min(i, nextCells.length - 1);
        placeInCell(tr, nextCells[i], false);
        return true;
    }
    placeInCell(tr, cells[Math.max(0, i)], true);
    return true;
}

/* ------------------------------------------------------- keys (commands) */

const toCommand = (fn) => (state, dispatch) => {
    const tr = state.tr;
    if (!fn(tr)) return false;
    if (dispatch) dispatch(tr.scrollIntoView());
    return true;
};

/** Enter by block type (C1 §1.14). Returns false to fall through to splitBlock. */
export const wikiEnter = toCommand((tr) => {
    const sel = tr.selection;
    if (!(sel instanceof TextSelection)) return false;
    const { $from, $to, empty } = sel;
    const parent = $from.parent;
    const { schema } = tr.doc.type;
    if (isCellNode(parent)) return moveTableCell(tr, 'down');
    if (parent.type.name === 'codeBlock') {
        if ($to.parent !== parent) return false;
        const before = parent.textBetween(0, $from.parentOffset);
        const after = clean(parent.textBetween($to.parentOffset, parent.content.size)).replace(/\n/g, '');
        if (empty && !after.length && /\n$/.test(before)) {
            // "Enter twice exits": trim the trailing newlines, new paragraph after.
            const trimmed = before.replace(/\n+$/, '');
            const start = $from.start();
            tr.replaceWith(start, $from.end(), trimmed ? schema.text(trimmed) : Fragment.empty);
            const blockEnd = start + trimmed.length + 1;
            tr.insert(blockEnd, schema.nodes.paragraph.create());
            tr.setSelection(TextSelection.create(tr.doc, blockEnd + 1));
            return true;
        }
        tr.insertText('\n', $from.pos, $to.pos);
        return true;
    }
    const top = $from.depth === 1;
    if (top && parent.type.name === 'paragraph') {
        const t = clean(parent.textContent).trim();
        if (/^`{3}[\w-]*$/.test(t) || /^(-{3,}|\*{3,}|_{3,})$/.test(t)) {
            tr.delete($from.start(), $from.end());
            tr.setSelection(TextSelection.create(tr.doc, $from.start()));
            return t[0] === '`' ? applyBlockKind(tr, 'pre') : insertBlocks(tr, [schema.nodes.horizontalRule.create()]);
        }
    }
    const atEnd = !clean(parent.textBetween($from.parentOffset, parent.content.size, '', '')).length;
    if (top && empty && atEnd && (parent.type.name === 'heading' || parent.type.name === 'blockquote')) {
        if (parent.type.name === 'blockquote' && !clean(parent.textContent).trim()) {
            tr.setNodeMarkup($from.before(), schema.nodes.paragraph);
            return true;
        }
        const pos = $from.after();
        tr.insert(pos, schema.nodes.paragraph.create());
        tr.setSelection(TextSelection.create(tr.doc, pos + 1));
        return true;
    }
    if (isItemNode(parent) && !clean(parent.textContent).trim()) return applyBlockKind(tr, 'p');
    return false;
});

/** Shift+Enter: a line break (newline in code; nothing in a plain-text cell). */
export const wikiShiftEnter = toCommand((tr) => {
    const { $from, $to } = tr.selection;
    const parent = $from.parent;
    if (isCellNode(parent)) return true;
    if (parent.type.name === 'codeBlock') {
        tr.insertText('\n', $from.pos, $to.pos);
        return true;
    }
    if (!parent.inlineContent) return false;
    tr.replaceSelectionWith(tr.doc.type.schema.nodes.hardBreak.create(), true);
    return true;
});

/** Backspace at a block's start: heading/quote/callout/code → text; list item → text between the list halves. */
export const wikiBackspace = toCommand((tr) => {
    const sel = tr.selection;
    if (!(sel instanceof TextSelection) || !sel.empty) return false;
    const { $from } = sel;
    if ($from.parentOffset !== 0) return false;
    const parent = $from.parent;
    if (isItemNode(parent)) return applyBlockKind(tr, 'p');
    if ($from.depth === 1 && ['heading', 'blockquote', 'codeBlock'].includes(parent.type.name)) return applyBlockKind(tr, 'p');
    return false;
});

/** Tab / Shift+Tab: table cell navigation, two spaces in code, otherwise nothing (no list nesting). */
export function wikiTab(dir) {
    return toCommand((tr) => {
        const { $from } = tr.selection;
        if (cellDepth($from)) return moveTableCell(tr, dir < 0 ? 'prev' : 'next');
        if ($from.parent.type.name === 'codeBlock') {
            tr.insertText('  ');
            return true;
        }
        return true;
    });
}

export { toCommand };
